import { useCallback, useEffect, useState } from 'react'

import { OrgPluginsAPI } from '@makyoapp/makyo-sdk'
import type { IPluginRefPresenter } from '@makyoapp/makyo-sdk/models/Plugins'

import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

export interface PluginCredential {
  id: string
  label: string
  pluginId: string
  picture: string
  working: boolean
}

export function usePluginCredentials(pluginId?: string) {
  const { currentOrg } = useCurrentOrgContext()
  const [credentials, setCredentials] = useState<PluginCredential[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchCredentials = useCallback(async () => {
    if (!currentOrg || !pluginId) return

    try {
      setLoading(true)
      const response = await OrgPluginsAPI.ReqGetOrgPluginsByRootPluginID(currentOrg.id, pluginId)

      // Map API response to just get id, label and pluginId
      const mappedCredentials = (response as unknown as IPluginRefPresenter[])
        .sort((a, b) => {
          const aDate = new Date(a._CreatedAt ?? 0).getTime()
          const bDate = new Date(b._CreatedAt ?? 0).getTime()
          return bDate - aDate
        })
        .map((credential) => ({
          id: credential._id,
          label: credential._Label,
          pluginId: credential._PluginID,
          name: credential._Label,
          value: credential._id,
          picture: credential?._Picture ?? '',
          working: credential?._Working ?? false,
        }))

      setCredentials(mappedCredentials)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch plugin credentials'))
      setCredentials([])
    } finally {
      setLoading(false)
    }
  }, [currentOrg, pluginId])

  useEffect(() => {
    fetchCredentials()
  }, [fetchCredentials])

  const refetch = () => {
    fetchCredentials()
  }

  return { credentials, loading, error, refetch }
}
